<template>
  <div
    v-if="fadeIn && image?.id && imageProps"
    :class="['lazy-image-container', { loaded, 'has-skeleton': hasSkeleton }]"
    data-allow-mismatch="class"
  >
    <img
      ref="imgEl"
      v-bind="imageProps.imgAttrs"
      :width="intrinsicWidth"
      :height="intrinsicHeight"
      :alt="alt ?? image.label ?? ''"
      class="lazy-image"
      onload="this.parentNode.classList.add('loaded')"
      @load="loaded = true"
    />
  </div>
  <img
    v-else-if="image?.id && imageProps"
    ref="imgEl"
    v-bind="imageProps.imgAttrs"
    :width="intrinsicWidth"
    :height="intrinsicHeight"
    :alt="alt ?? image.label ?? ''"
    :class="['plain-image', { loaded, 'has-skeleton': hasSkeleton }]"
    @load="loaded = true"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useImagePreset, type ImageFieldData } from '@render/composables/useImagePreset'

const props = withDefaults(
  defineProps<{
    image?: ImageFieldData
    /**
     * Overrides the media library's own `image.label` as the accessible name.
     * Declared as a real prop (not left to fall through) because the fadeIn
     * root is a wrapper <div> — a fallthrough `alt` would land there, where it
     * means nothing, instead of on the <img>. Pass `''` for a decorative image.
     */
    alt?: string
    skeletonColor?: string
    shimmerColor?: string
    fadeIn?: boolean
  }>(),
  {
    skeletonColor: 'transparent',
    shimmerColor: 'transparent',
    // Ability to turn off the fade-in effect on lazy load, which requires an extra div wrapper
    fadeIn: true,
  },
)

const imageProps = useImagePreset(() => props.image)
const imgEl = ref<HTMLImageElement | null>(null)
const loaded = ref(false)
watch(
  () => props.image?.id,
  () => {
    loaded.value = false
  },
)

// The media library records 0×0 for a file whose intrinsic size it could not
// read — an SVG sized in percentages, or with no viewBox to fall back on. Passed
// through, that becomes `<img width="0" height="0">`, which is a picture the
// browser is being told has no area, and the component renders nothing at all.
// Omitting the attributes instead lets the SVG size itself, which is the only
// dimension anyone has.
const intrinsicWidth = computed(() => props.image?.width || undefined)
const intrinsicHeight = computed(() => props.image?.height || undefined)

// Built pipes are Nuxt-prerendered, so the browser fetches images from the
// SSR HTML before JS runs, and on a slow connection a picture arrives seconds
// before the app has downloaded and hydrated enough to attach @load. So the
// fade-in is started by the <img>'s static `onload` attribute, which is in the
// HTML and which hydration leaves alone. It writes only the DOM (hence
// `data-allow-mismatch`: hydration can find `.loaded` already there), so this
// sync tells `loaded` about a load that happened before hydration attached
// @load; without it the next class patch rewrites className without `.loaded`
// and the image drops back to opacity: 0.
//
// `naturalWidth > 0` is what separates "complete because it loaded" from
// "complete because it failed" — except for a vector with no intrinsic size,
// where a successful load also reports 0. Those stayed at opacity: 0 forever,
// which is the invisible-SVG bug: the file was fetched, decoded and laid out,
// and only this guard kept it transparent.
onMounted(() => {
  const el = imgEl.value
  if (!el?.complete) return
  if (el.naturalWidth > 0 || props.image?.mimeType === 'image/svg+xml') {
    loaded.value = true
  }
})

// Skeleton animation has some costs by continuously repainting while loading.
// When transparent color is set we skip the entire animation pipeline.
const hasSkeleton = computed(
  () => props.skeletonColor !== 'transparent' || props.shimmerColor !== 'transparent',
)
</script>

<style scoped>
/* Regular mode (fadeIn = false) - styles applied directly to the img element */

.plain-image.has-skeleton {
  background-color: v-bind('props.skeletonColor');
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    v-bind('props.shimmerColor') 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: plain-image-shimmer 1.4s linear infinite;
  transition: background-color 0.25s ease;
}

.plain-image.has-skeleton.loaded {
  background-color: transparent;
  background-image: none;
  animation: none;
}

/* Lazy fade-in mode - container holds bg+shimmer, img gets its own opacity transition */

.lazy-image-container {
  position: relative;
}

.lazy-image-container.has-skeleton {
  background-color: v-bind('props.skeletonColor');
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    v-bind('props.shimmerColor') 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: plain-image-shimmer 1.4s linear infinite;
  transition: background-color 0.25s ease;
}

.lazy-image-container.has-skeleton.loaded {
  background-color: transparent;
  background-image: none;
  animation: none;
}

.lazy-image-container .lazy-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: inherit;
  object-position: inherit;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.lazy-image-container.loaded .lazy-image {
  opacity: 1;
}

@keyframes plain-image-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
